import { useLanguage } from "@/contexts/LanguageContext";
import { CheckCircle2, AlertCircle, Loader2, AlertTriangle } from "lucide-react";
import { cn } from "@/lib/utils";

interface AutoSaveIndicatorProps {
  status: 'idle' | 'saving' | 'saved' | 'error' | 'validating' | 'retrying';
  className?: string;
  lastSaveTime?: Date;
  validationErrors?: string[];
}

export const AutoSaveIndicator = ({ 
  status, 
  className,
  lastSaveTime,
  validationErrors 
}: AutoSaveIndicatorProps) => {
  const { language } = useLanguage();

  if (status === 'idle') return null;

  const getTimeAgo = () => {
    if (!lastSaveTime) return '';
    const seconds = Math.floor((Date.now() - lastSaveTime.getTime()) / 1000);
    if (seconds < 60) return language === 'ar' ? 'للتو' : 'just now';
    const minutes = Math.floor(seconds / 60);
    return language === 'ar' ? `منذ ${minutes} د` : `${minutes}m ago`;
  };

  return (
    <div className={cn(
      "flex flex-col gap-1 text-sm transition-all duration-300",
      className
    )}>
      <div className={cn(
        "flex items-center gap-2",
        status === 'saving' && "text-muted-foreground",
        status === 'validating' && "text-blue-600",
        status === 'saved' && "text-emerald-600",
        status === 'error' && "text-destructive",
        status === 'retrying' && "text-orange-600"
      )}>
        {status === 'validating' && (
          <>
            <Loader2 className="h-4 w-4 animate-spin" />
            <span>{language === 'ar' ? 'جارٍ التحقق...' : 'Validating...'}</span>
          </>
        )}
        {status === 'saving' && (
          <>
            <Loader2 className="h-4 w-4 animate-spin" />
            <span>{language === 'ar' ? 'جارٍ الحفظ...' : 'Saving...'}</span>
          </>
        )}
        {status === 'saved' && (
          <>
            <CheckCircle2 className="h-4 w-4" />
            <span>{language === 'ar' ? 'تم الحفظ' : 'Saved'} {getTimeAgo()}</span>
          </>
        )}
        {status === 'retrying' && (
          <>
            <Loader2 className="h-4 w-4 animate-spin" />
            <span>{language === 'ar' ? 'إعادة المحاولة...' : 'Retrying...'}</span>
          </>
        )}
        {status === 'error' && (
          <>
            <AlertCircle className="h-4 w-4" />
            <span>{language === 'ar' ? 'فشل الحفظ' : 'Save failed'}</span>
          </>
        )}
      </div>
      
      {/* Show validation errors if any */}
      {validationErrors && validationErrors.length > 0 && (
        <div className="flex items-start gap-2 text-xs text-orange-600">
          <AlertTriangle className="h-3 w-3 mt-0.5 flex-shrink-0" />
          <div className="flex flex-col gap-0.5">
            {validationErrors.slice(0, 2).map((error, index) => (
              <span key={index}>{error}</span>
            ))}
            {validationErrors.length > 2 && (
              <span className="text-muted-foreground">
                +{validationErrors.length - 2} more
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
